/**
 * Reports Page
 * Vista para visualizar informes y estadísticas del negocio
 */

import { useEffect, useState } from "react";
import { Icon } from "@/components/ui";
import {
  reportsService,
  type APIReportTechnicians,
  type APIReportCosts,
  type APIReportMaterialConsumption,
  type APIReportKPIs,
} from "@/services/reportsService";
import styles from "./ReportsPage.module.css";

type ReportPeriod = "week" | "month" | "quarter" | "year";
type ReportType = "technicians" | "costs" | "materials" | "kpis";

export const ReportsPage = () => {
  const [period, setPeriod] = useState<ReportPeriod>("month");
  const [reportType, setReportType] = useState<ReportType>("technicians");

  const [technicians, setTechnicians] = useState<APIReportTechnicians>([]);
  const [costs, setCosts] = useState<APIReportCosts | null>(null);
  const [materials, setMaterials] = useState<APIReportMaterialConsumption>([]);
  const [kpis, setKpis] = useState<APIReportKPIs | null>(null);
  const [isLoadingReports, setIsLoadingReports] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoadingReports(true);
      setTechnicians([]);
      setCosts(null);
      setMaterials([]);
      setKpis(null);

      try {
        const [techs, costsRes, materialsRes, kpisRes] =
          await Promise.allSettled([
            reportsService.getTechnicians(period),
            reportsService.getCosts(period),
            reportsService.getMaterialConsumption(period),
            reportsService.getKPIs(period),
          ]);

        if (techs.status === "fulfilled") setTechnicians(techs.value);
        if (costsRes.status === "fulfilled") setCosts(costsRes.value);
        if (materialsRes.status === "fulfilled")
          setMaterials(materialsRes.value);
        if (kpisRes.status === "fulfilled") setKpis(kpisRes.value);
      } catch (err) {
        console.error("Error cargando informes:", err);
      } finally {
        setIsLoadingReports(false);
      }
    };

    void fetchAll();
  }, [period]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const getPeriodLabel = (value: ReportPeriod): string => {
    const labels = {
      week: "Esta Semana",
      month: "Este Mes",
      quarter: "Este Trimestre",
      year: "Este Año",
    };
    return labels[value];
  };

  const costsByClient = costs?.byClient ?? [];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1>📊 Informes y Estadísticas</h1>
          <p className={styles.subtitle}>
            Análisis detallado del desempeño operativo
            {isLoadingReports && (
              <span
                style={{
                  marginLeft: "0.5rem",
                  fontSize: "0.8em",
                  opacity: 0.6,
                }}
              >
                Actualizando...
              </span>
            )}
          </p>
        </div>
      </div>

      <div className={styles.controls}>
        <div className={styles.periodSelector}>
          <button
            className={`${styles.periodButton} ${period === "week" ? styles.active : ""}`}
            onClick={() => setPeriod("week")}
          >
            Semana
          </button>
          <button
            className={`${styles.periodButton} ${period === "month" ? styles.active : ""}`}
            onClick={() => setPeriod("month")}
          >
            Mes
          </button>
          <button
            className={`${styles.periodButton} ${period === "quarter" ? styles.active : ""}`}
            onClick={() => setPeriod("quarter")}
          >
            Trimestre
          </button>
          <button
            className={`${styles.periodButton} ${period === "year" ? styles.active : ""}`}
            onClick={() => setPeriod("year")}
          >
            Año
          </button>
        </div>

        <div className={styles.reportTypeSelector}>
          <button
            className={`${styles.typeButton} ${reportType === "technicians" ? styles.active : ""}`}
            onClick={() => setReportType("technicians")}
          >
            <Icon name="users" size={18} />
            Por Técnico
          </button>
          <button
            className={`${styles.typeButton} ${reportType === "costs" ? styles.active : ""}`}
            onClick={() => setReportType("costs")}
          >
            <Icon name="creditCard" size={18} />
            Costes
          </button>
          <button
            className={`${styles.typeButton} ${reportType === "materials" ? styles.active : ""}`}
            onClick={() => setReportType("materials")}
          >
            <Icon name="document" size={18} />
            Materiales
          </button>
          <button
            className={`${styles.typeButton} ${reportType === "kpis" ? styles.active : ""}`}
            onClick={() => setReportType("kpis")}
          >
            <Icon name="dashboard" size={18} />
            KPIs
          </button>
        </div>
      </div>

      {reportType === "technicians" && technicians.length > 0 && (
        <div className={styles.reportSection}>
          <div className={styles.sectionHeader}>
            <h2>👷 Servicios por Técnico - {getPeriodLabel(period)}</h2>
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th>Técnico</th>
                  <th>Servicios</th>
                  <th>Horas Trabajadas</th>
                  <th>Eficiencia</th>
                  <th>Ingresos Generados</th>
                  <th>Ingreso/Servicio</th>
                </tr>
              </thead>
              <tbody>
                {technicians.map((tech, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{tech.name}</strong>
                    </td>
                    <td>{tech.services}</td>
                    <td>{tech.hours}h</td>
                    <td>
                      <span className={styles.badge}>{tech.efficiency}%</span>
                    </td>
                    <td>{formatCurrency(tech.revenue)}</td>
                    <td>
                      {typeof tech.incomePerService === "number"
                        ? formatCurrency(tech.incomePerService)
                        : tech.services > 0
                          ? formatCurrency(tech.revenue / tech.services)
                          : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reportType === "costs" && costsByClient.length > 0 && (
        <div className={styles.reportSection}>
          <div className={styles.sectionHeader}>
            <h2>💰 Análisis de Costes - {getPeriodLabel(period)}</h2>
          </div>

          <h3 className={styles.subsectionTitle}>Por Cliente</h3>
          <div className={styles.tableContainer}>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Órdenes de trabajo</th>
                  <th>Coste materiales</th>
                  <th>Coste por orden</th>
                </tr>
              </thead>
              <tbody>
                {costsByClient.map((client, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{client.name}</strong>
                    </td>
                    <td>{client.services}</td>
                    <td>{formatCurrency(client.materials)}</td>
                    <td>
                      {client.services > 0
                        ? formatCurrency(client.materials / client.services)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reportType === "materials" && materials.length > 0 && (
        <div className={styles.reportSection}>
          <div className={styles.sectionHeader}>
            <h2>📦 Consumo de Materiales - {getPeriodLabel(period)}</h2>
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Cantidad Consumida</th>
                  <th>Coste Total</th>
                  <th>Servicios Realizados</th>
                  <th>Consumo/Servicio</th>
                  <th>Coste/Servicio</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((material, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{material.material}</strong>
                    </td>
                    <td>
                      {material.quantity} {material.unit}
                    </td>
                    <td>{formatCurrency(material.cost)}</td>
                    <td>{material.services}</td>
                    <td>
                      {material.services > 0
                        ? `${(material.quantity / material.services).toFixed(2)} ${material.unit}`
                        : "—"}
                    </td>
                    <td>
                      {material.services > 0
                        ? formatCurrency(material.cost / material.services)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reportType === "kpis" && kpis && (
        <div className={styles.reportSection}>
          <div className={styles.sectionHeader}>
            <h2>📈 KPIs Operativos - {getPeriodLabel(period)}</h2>
          </div>
          <div className={styles.kpisGrid}>
            <div className={styles.kpiCard}>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>Servicios Completados</span>
                <span className={styles.kpiValue}>
                  {kpis.servicesCompleted}
                </span>
              </div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>
                  Tiempo de Respuesta Promedio
                </span>
                <span className={styles.kpiValue}>{kpis.avgResponseTime}h</span>
              </div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>
                  Satisfacción del Cliente
                </span>
                <span className={styles.kpiValue}>
                  {kpis.customerSatisfaction}%
                </span>
              </div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>Eficiencia de Técnicos</span>
                <span className={styles.kpiValue}>
                  {kpis.technicianEfficiency}%
                </span>
              </div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>
                  Utilización de Materiales
                </span>
                <span className={styles.kpiValue}>
                  {kpis.materialUtilization}%
                </span>
              </div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>Ingreso por Servicio</span>
                <span className={styles.kpiValue}>
                  {formatCurrency(kpis.revenuePerService)}
                </span>
              </div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>Coste por Servicio</span>
                <span className={styles.kpiValue}>
                  {formatCurrency(kpis.costPerService)}
                </span>
              </div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel}>Margen de Beneficio</span>
                <span className={styles.kpiValue}>{kpis.profitMargin}%</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
